import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import { Add } from "@mui/icons-material";
import { MiniSection } from "./MiniSection";
import { SectionInsertDivider } from "./SectionInsertDivider";
import { Dialog } from "../common/Dialog";
import { SectionTemplatePicker } from "./SectionTemplatePicker";
import {
  useScrollytellState,
  useScrollytellTranslation,
} from "./scrollytellContext";

const useStyles = createUseStyles({
  sectionsWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    paddingBottom: "8px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    backgroundColor: "#0F172A",
    padding: "10px 12px",
    borderTop: "1px solid rgba(255, 255, 255, 0.12)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  addSectionButton: {
    width: "100%",
    height: "38px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#4484F1",
    border: "none",
    borderRadius: "6px",
    color: "#FFFFFF",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    gap: "6px",
    transition: "background-color 150ms ease",
    "&:hover": {
      backgroundColor: "#3367D6",
    },
  },
  footerMeta: {
    fontSize: "11px",
    color: "#94A3B8",
  },
});

export const Sections = ({ sections = [], currentSection, compactMode = false }) => {
  const classes = useStyles();
  const t = useScrollytellTranslation();
  const { activeElement } = useScrollytellState();
  const [insertModalIndex, setInsertModalIndex] = useState(null);

  const handleOpenBottomAdd = () => {
    const activeSecIdx = activeElement?.[0];
    if (typeof activeSecIdx === "number" && activeSecIdx >= 0 && activeSecIdx < sections.length) {
      setInsertModalIndex(activeSecIdx + 1);
    } else {
      setInsertModalIndex(sections.length);
    }
  };

  const activeSecIdx = activeElement?.[0];
  const hasActiveChoice =
    typeof activeSecIdx === "number" && activeSecIdx >= 0 && activeSecIdx < sections.length && sections.length > 1;

  return (
    <div className={classes.sectionsWrapper}>
      <div className={classes.list}>
        {sections.map((section, i) => (
          <React.Fragment key={section.order ?? i}>
            <SectionInsertDivider index={i} onInsert={() => setInsertModalIndex(i)} />
            <MiniSection
              index={i}
              section={section}
              currentSection={currentSection}
              totalSections={sections.length}
              compactMode={compactMode}
              onInsertAt={(idx) => setInsertModalIndex(idx)}
            />
          </React.Fragment>
        ))}
        <SectionInsertDivider index={sections.length} onInsert={() => setInsertModalIndex(sections.length)} />
      </div>

      <div className={classes.stickyFooter}>
        <button
          className={classes.addSectionButton}
          onClick={handleOpenBottomAdd}
          data-testid="add-section-button"
        >
          <Add fontSize="small" />
          {t("newSectionButton", "Ny seksjon")}
        </button>
        <span className={classes.footerMeta}>
          {sections.length === 1
            ? t("sidebar.sectionCountSingular", "{{count}} seksjon", { count: sections.length })
            : t("sidebar.sectionCountPlural", "{{count}} seksjoner", { count: sections.length })}
        </span>
      </div>

      <Dialog
        isOpen={insertModalIndex !== null}
        onClose={() => setInsertModalIndex(null)}
        title={t("sectionTemplateModalTitle", "Sett inn ny seksjon")}
      >
        {hasActiveChoice && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              marginBottom: "12px",
              padding: "6px 12px",
              backgroundColor: "#F1F5F9",
              borderRadius: "6px",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
              {t("insertPlacementLabel", "Plassering:")}
            </span>
            <button
              type="button"
              onClick={() => setInsertModalIndex(activeSecIdx + 1)}
              style={{
                fontSize: "11px",
                padding: "3px 8px",
                borderRadius: "4px",
                border: "1px solid #CBD5E1",
                backgroundColor: insertModalIndex === activeSecIdx + 1 ? "#4484F1" : "#FFFFFF",
                color: insertModalIndex === activeSecIdx + 1 ? "#FFFFFF" : "#1E293B",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {t("afterActiveSection", "Etter aktiv")} (#{activeSecIdx + 1})
            </button>
            <button
              type="button"
              onClick={() => setInsertModalIndex(sections.length)}
              style={{
                fontSize: "11px",
                padding: "3px 8px",
                borderRadius: "4px",
                border: "1px solid #CBD5E1",
                backgroundColor: insertModalIndex === sections.length ? "#4484F1" : "#FFFFFF",
                color: insertModalIndex === sections.length ? "#FFFFFF" : "#1E293B",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {t("atTheEnd", "Nederst på slutten")} (#{sections.length + 1})
            </button>
          </div>
        )}
        <SectionTemplatePicker
          insertIndex={insertModalIndex ?? sections.length}
          onClose={() => setInsertModalIndex(null)}
        />
      </Dialog>
    </div>
  );
};
